// TODO: Move types to respective files
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FieldPlannerTab } from 'features/Yucollect/AgencyMonitor/types/AllocationListDataType';
import { Column } from 'features/Yucollect/utils/types';
import {
  IFailedPayload,
  IGetFieldAgentsRequest,
  IGetFieldAgentsResponse,
  IScheduleVisitRequest,
  IUnassignFieldAgentRequest,
} from 'redux/types/Call.types';

export interface FieldPlannerState {
  error: IFailedPayload | null;
  loading: boolean;
  manualAssignLoading: boolean;
  unassignLoading: boolean;
  reassignLoading: boolean;
  scheduleVisitLoading: boolean;
  message: string;
  fieldAgents: IGetFieldAgentsResponse[];
  assignAgentsMessage: string;
  unAssignAgentsMessage: string;
  reAssignAgentsMessage: string;
  scheduleVisitMessage: string;
  reScheduleVisitMessage: string;
  activeFieldPlannerTab: string;
  isAutoAssignLoading: boolean;
}

export const fieldPlannerInitState: FieldPlannerState = {
  error: null,
  loading: false,
  manualAssignLoading: false,
  unassignLoading: false,
  reassignLoading: false,
  scheduleVisitLoading: false,
  message: '',
  fieldAgents: [],
  assignAgentsMessage: '',
  unAssignAgentsMessage: '',
  reAssignAgentsMessage: '',
  scheduleVisitMessage: '',
  reScheduleVisitMessage: '',
  activeFieldPlannerTab: FieldPlannerTab.UNALLOCATED_ACCOUNTS,
  isAutoAssignLoading: false,
};

const fieldPlannerSlice = createSlice({
  name: 'fieldPlanner',
  initialState: fieldPlannerInitState,
  reducers: {
    // Auto assignment
    autoAssignAgent: (
      state,
      action: PayloadAction<{ allocationId: string }>
    ) => {
      state.isAutoAssignLoading = true;
      state.error = null;
    },
    autoAssignAgentSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.isAutoAssignLoading = false;
      state.message = action.payload.message;
    },
    autoAssignAgentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.isAutoAssignLoading = false;
    },
    // Manual field agent assignment
    manualAssignAgent: (
      state,
      action: PayloadAction<{
        columns: Column[];
        supervisorId: string;
        selectedBorrowerIds: string[];
        unselectedBorrowerIds: string[];
        allocationId: string;
        agentId: string;
        applyToAllAccounts: boolean;
        ungrouped: boolean;
        isAllocated: boolean;
      }>
    ) => {
      state.manualAssignLoading = true;
      state.error = null;
    },
    manualAssignAgentSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      // state.message = action.payload.message;
      state.manualAssignLoading = false;
      state.assignAgentsMessage = action.payload.message;
    },
    manualAssignAgentFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.manualAssignLoading = false;
    },
    // Manual field agent assignment
    reAssignAgent: (
      state,
      action: PayloadAction<{
        columns: Column[];
        supervisorId: string;
        selectedBorrowerIds: string[];
        unselectedBorrowerIds: string[];
        allocationId: string;
        agentId: string;
        applyToAllAccounts: boolean;
        ungrouped: boolean;
        isAllocated: boolean;
      }>
    ) => {
      state.reassignLoading = true;
      state.error = null;
    },
    reAssignAgentSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      // state.message = action.payload.message;
      state.reassignLoading = false;
      state.reAssignAgentsMessage = action.payload.message;
    },
    reAssignAgentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.reassignLoading = false;
    },
    // Unassign agent
    unAssignAgent: (
      state,
      action: PayloadAction<IUnassignFieldAgentRequest>
    ) => {
      state.unassignLoading = true;
      state.error = null;
    },
    unAssignAgentSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      // state.message = action.payload.message;
      state.unassignLoading = false;
      state.unAssignAgentsMessage = action.payload.message;
    },
    unAssignAgentFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.unassignLoading = false;
    },
    // Saga for getting agents by agency and channel
    getFieldAgents: (state, action: PayloadAction<IGetFieldAgentsRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getFieldAgentsSuccess: (
      state,
      action: PayloadAction<IGetFieldAgentsResponse[]>
    ) => {
      state.fieldAgents = action.payload;
    },
    getFieldAgentsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Saga for getting agents by agency and channel
    scheduleVisit: (state, action: PayloadAction<IScheduleVisitRequest>) => {
      state.scheduleVisitLoading = true;
      state.error = null;
    },
    scheduleVisitSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      // state.message = action.payload;
      state.scheduleVisitLoading = false;
      state.scheduleVisitMessage = action.payload.message;
    },
    scheduleVisitFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.scheduleVisitLoading = false;
    },
    resetFieldPlannerMessage: (state) => {
      state.message = '';
      state.assignAgentsMessage = '';
      state.unAssignAgentsMessage = '';
      state.reAssignAgentsMessage = '';
      state.scheduleVisitMessage = '';
      state.reScheduleVisitMessage = '';
    },
    setActiveFieldPlannerTab: (state, action: PayloadAction<string>) => {
      state.activeFieldPlannerTab = action.payload;
    },
  },
});

export const {
  autoAssignAgent,
  autoAssignAgentSuccess,
  autoAssignAgentFailure,
  manualAssignAgent,
  manualAssignAgentSuccess,
  manualAssignAgentFailure,
  reAssignAgent,
  reAssignAgentSuccess,
  reAssignAgentFailure,
  unAssignAgent,
  unAssignAgentSuccess,
  unAssignAgentFailure,
  getFieldAgents,
  getFieldAgentsSuccess,
  getFieldAgentsFailure,
  scheduleVisit,
  scheduleVisitSuccess,
  scheduleVisitFailure,
  resetFieldPlannerMessage,
  setActiveFieldPlannerTab,
} = fieldPlannerSlice.actions;

export default fieldPlannerSlice.reducer;
